import React, { useContext } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import { createKeyword } from "../../../api/legacy/ContentAPI";
import ContentContext from "../../../contexts/ContentContext";
import { getTranslatedMetadata } from "../../../utils/data/Content";
import { getMetadatas, getMetaValues } from "../../../utils/data/metadata";

import type { JSX } from "react";
import type { MultiValue } from "react-select";

import type {
  Category,
  CreateKeywordResponse,
  GetMediaResponse,
  GetMediasMetadataListResponse,
  MediaUpdated,
} from "../../../api/MediaAPI.types";
import type { AppState } from "../../../reducers";
import type { KeyWithTranslation } from "../../../types";

interface Classification {
  title: string;
  type: string;
  value: Option | MultiValue<Option> | null;
  cb: (value: Option | MultiValue<Option> | null) => void;
  multi?: boolean;
  isDisabled?: boolean;
  tooltipContent?: string;
}

interface Keyword {
  label: string;
  value: string;
  data: {
    key: string;
    uuid: string;
  };
}

interface Option {
  value: string;
  label: string;
  data?: KeyWithTranslation;
}

interface ContentClassificationProps {
  isMasterFieldDisabled?: boolean;
  masterFieldTooltip?: string;
}

function ContentClassification({
  isMasterFieldDisabled,
  masterFieldTooltip,
}: ContentClassificationProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const {
    item,
    onItemUpdate,
    languageId,
    isDisabled: isDisabledContent,
    metadatas,
  } = useContext<{
    item: GetMediaResponse;
    onItemUpdate: (media: MediaUpdated) => void;
    languageId: string;
    isDisabled: boolean;
    metadatas: GetMediasMetadataListResponse;
  }>(ContentContext);

  const dispatch = useDispatch();

  const token = useSelector((state: AppState) => state.loginReducer.token);

  const addKeyword = async (data: {
    key: string;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  }): Promise<CreateKeywordResponse> => dispatch(createKeyword(data, token));

  const handleKeyUpdate = (
    selectedValue: Option | MultiValue<Option> | null,
    key: string,
  ): void => {
    if (key === "genres") {
      const updatedKey = (selectedValue as Option)?.data
        ? [(selectedValue as Option).data]
        : [];
      onItemUpdate({ ...item, [key]: updatedKey as Category[] });
      return;
    }
    if (key === "themes" || key === "accessibilities") {
      const updatedKey = (selectedValue as MultiValue<Option>)?.length
        ? (selectedValue as MultiValue<Option>).map(({ data }) => ({
            ...data,
          }))
        : [];
      onItemUpdate({ ...item, [key]: updatedKey });
      return;
    }
    const updatedKey = (selectedValue as Option)?.data ?? null;
    onItemUpdate({ ...item, [key]: updatedKey });
  };

  const classifications: Classification[] = [
    {
      title: "category",
      type: "categories",
      value: item.category
        ? {
            value: item.category.uuid,
            label: t(`content:categories.${item.category.key}`),
          }
        : null,
      cb: (value): void => handleKeyUpdate(value, "category"),
    },
    {
      title: "classification",
      type: "genres",
      value: item.genres[0]
        ? {
            value: item.genres[0].uuid,
            label: t(`content:genres.${item.genres[0].key}`),
          }
        : null,
      cb: (value): void => handleKeyUpdate(value, "genres"),
    },
    {
      title: "genre",
      type: "new_genres",
      value: item.new_genre
        ? {
            value: item.new_genre.uuid,
            label: getTranslatedMetadata(item.new_genre, "title", languageId),
          }
        : null,
      cb: (value): void => handleKeyUpdate(value, "new_genre"),
    },
    {
      title: "format",
      type: "formats",
      value: item.format
        ? {
            value: item.format.uuid,
            label: getTranslatedMetadata(item.format, "title", languageId),
          }
        : null,
      isDisabled: item.is_in_adbreak,
      tooltipContent: item.is_in_adbreak
        ? t("tooltipMessageFormatFieldDisabled")
        : undefined,
      cb: (value): void => handleKeyUpdate(value, "format"),
    },
    {
      title: "theme",
      type: "themes",
      multi: true,
      value: getMetaValues({
        metadata: item.themes,
        languageId,
      }),
      cb: (value): void => handleKeyUpdate(value, "themes"),
    },
    {
      title: "target_audience",
      type: "target_audiences",
      value: item.target_audience
        ? {
            value: item.target_audience.uuid,
            label: getTranslatedMetadata(
              item.target_audience,
              "title",
              languageId,
            ),
          }
        : null,
      cb: (value): void => handleKeyUpdate(value, "target_audience"),
    },
    {
      title: "accessibility",
      type: "accessibilities",
      multi: true,
      value: getMetaValues({
        metadata: item.accessibilities,
        languageId,
      }),
      cb: (value): void => handleKeyUpdate(value, "accessibilities"),
    },
  ];

  const handleNewKeyword = (newKeywordKey: string): void => {
    addKeyword({ key: newKeywordKey }).then((response) => {
      response.keywords.forEach((keyword) => {
        if (keyword.key === newKeywordKey) {
          const updatedItem = {
            ...item,
            keywords: [...item.keywords, keyword],
          };

          onItemUpdate(updatedItem);
        }
      });
    });
  };
  const handleKeywordsChange = (keywords: MultiValue<Keyword>): void => {
    const updatedKeywords = keywords?.map((keyword) => keyword.data);
    onItemUpdate({
      ...item,
      keywords: updatedKeywords || [],
    });
  };

  const buildClassificationEdit = (
    classification: Classification,
  ): JSX.Element => {
    const {
      title,
      value,
      cb,
      type,
      multi,
      isDisabled: isDisabledField,
      tooltipContent,
    } = classification;
    return (
      <div key={`classification_${title}`} className="editor-form-section">
        <div className="editor-form-section-title">{t(title)}</div>
        <div
          className="editor-form-input"
          data-tooltip-id={APP_TOOLTIP_ID}
          data-tooltip-content={
            isMasterFieldDisabled ? masterFieldTooltip : tooltipContent
          }
        >
          <Select
            closeMenuOnSelect
            isClearable
            className="editor-select"
            classNamePrefix="slct"
            isMulti={multi}
            name="form-field-name"
            onChange={cb}
            placeholder={t("select")}
            value={value}
            isDisabled={
              isDisabledContent || isDisabledField || isMasterFieldDisabled
            }
            noOptionsMessage={({ inputValue }) =>
              !inputValue && t("noOptions", { ns: "forms" })
            }
            options={getMetadatas({
              metadatas,
              type,
              languageId,
              withTranslations: true,
            })}
          />
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="editor-form-grid">
        {classifications.map((classification) =>
          buildClassificationEdit(classification),
        )}
      </div>

      <div className="editor-form-section section-separator">
        <div className="editor-form-section-title">{t("keywords")}</div>
        <div
          className="editor-form-input"
          data-tooltip-content={masterFieldTooltip}
          data-tooltip-id={APP_TOOLTIP_ID}
          style={{ maxWidth: "500px" }}
        >
          <CreatableSelect
            closeMenuOnSelect
            isMulti
            className="editor-select"
            classNamePrefix="slct"
            isDisabled={isDisabledContent || isMasterFieldDisabled}
            name="form-field-name"
            onChange={handleKeywordsChange}
            onCreateOption={handleNewKeyword}
            options={getMetadatas({ metadatas, type: "keywords", languageId })}
            placeholder={t("select")}
            value={getMetaValues({ metadata: item.keywords, languageId })}
          />
        </div>
      </div>
    </React.Fragment>
  );
}

export default ContentClassification;
